import { Box, Stack } from '@mui/material';
import ButtonMui from '@mui/material/Button';
import Button from 'new/components/Button/Button';
import { styled } from '@mui/material/styles';
import IconFontAwesome from 'new/components/IconFontAwesome';
import { boxShadow } from 'new/constants/styles';
import {
  LandingPageCommonProps,
  OfficeInfoNoAuth,
} from 'new/modules/ExternalPatientSignup/types';
import findRoute from 'new/routing/routes';
import React from 'react';
import { useIntl } from 'react-intl';
import { history } from '../../../../app';
import LanguageToggle from './LanguageToggle';
import { commonLandingPageStyles } from '../styles';

const OfficeHeaderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showJoinButton',
})<{ showJoinButton: boolean }>(({ theme, showJoinButton }) => ({
  ...(showJoinButton ? commonLandingPageStyles : {}),
  width: '100%',
  backgroundColor: '#fff',
  boxShadow,
  zIndex: 2,

  '.officeInfo': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'center',
    marginRight: theme.spacing(5),

    '.phone': {
      fontSize: theme.typography.pxToRem(24),
      fontWeight: 'bold',
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: theme.spacing(2),
    },

    '.address': {
      fontSize: '0.875rem',
      fontWeight: 'bold',
      textAlign: 'left',
    },

    '.phone, .address': {
      whiteSpace: 'nowrap',
      overflow: 'auto',
    },
  },
}));

const redirectToLogin = () => {
  const loginRoute = findRoute('LoginPage').path;

  history.push(loginRoute);
};

type Props = {
  officeData: OfficeInfoNoAuth;
  isMobileView: boolean;
  showJoinButton?: boolean;
  hasSpanishPlans?: boolean;
  landingProps?: LandingPageCommonProps;
  colorTwoHex: string;
  groupCode?: string | null;
};

const OfficeHeader: React.FC<Props> = ({
  officeData,
  isMobileView,
  showJoinButton = false,
  hasSpanishPlans,
  landingProps,
  colorTwoHex,
  groupCode,
}) => {
  const intl = useIntl();
  const {
    logo,
    phone,
    zipCode,
    address,
    address2,
    city,
    state,
    url,
    officeName,
  } = officeData.data;
  let newUrl = url;
  if (!url.includes('http') && !url.includes('https')) {
    newUrl = `https://${url}`;
  }

  const showLanguageToggle = hasSpanishPlans === undefined || hasSpanishPlans;

  return (
    <OfficeHeaderWrapper
      height={isMobileView ? 60 : 95}
      showJoinButton={showJoinButton}
    >
      <Box display="flex" alignItems="center" height="100%" width="100%">
        <Box ml={4}>
          <a
            target="_blank"
            href={`${newUrl}`}
            onClick={(e) => {
              if (!url?.length) {
                e.preventDefault();
              }
            }}
          >
            {logo ? (
              <img
                height={isMobileView ? 30 : 60}
                src={logo}
                alt="Office Logo"
              />
            ) : (
              <span
                style={{
                  fontSize: '1.5rem',
                  color: colorTwoHex,
                  fontWeight: 'bold',
                }}
              >
                {officeName}
              </span>
            )}
          </a>
        </Box>
        <Box className="officeInfo">
          {isMobileView ? (
            <Box>
              <Box
                className="phone"
                fontSize="1rem !important"
                style={{ color: colorTwoHex }}
              >
                {phone}
              </Box>
              {showLanguageToggle && <LanguageToggle />}
            </Box>
          ) : (
            <Stack direction="row" alignItems="center">
              <Box>
                <Box className="officeInfo">
                  <Box className="phone" style={{ color: colorTwoHex }}>
                    {phone}
                  </Box>
                  <Box className="address">
                    {address},{address2 && ` ${address2},`}
                  </Box>
                  <Box className="address">
                    {city}, {state} {zipCode}
                  </Box>
                </Box>
              </Box>
              <Box width={250} pt={showJoinButton ? 0.5 : 2} textAlign="right">
                {showJoinButton && (
                  <Button
                    sx={{
                      height: 46,
                      lineHeight: 1.4,
                      backgroundColor: landingProps?.colorOneHex,
                      ...commonLandingPageStyles,
                    }}
                    to={`${findRoute('ExternalPatientSignup').path}?officeId=${
                      officeData.data.id
                    }${
                      groupCode == null || groupCode == undefined
                        ? ''
                        : '&groupCode=' + groupCode
                    }`}
                  >
                    <Stack>
                      <Box fontSize="1.15rem">
                        {intl.formatMessage({ id: 'label.join' })}
                      </Box>
                      <Box fontSize="0.6rem" fontWeight={400}>
                        {intl.formatMessage({ id: 'label.ourMembershipPlan' })}
                      </Box>
                    </Stack>
                  </Button>
                )}
                <ButtonMui
                  variant="text"
                  onClick={redirectToLogin}
                  sx={
                    showJoinButton
                      ? {
                          fontSize: '0.7rem',
                          padding: '0!important',
                          height: 20,
                        }
                      : undefined
                  }
                  style={{ color: colorTwoHex }}
                >
                  {intl.formatMessage({ id: 'label.alreadyMemberLogin' })}{' '}
                  <Box pl={1}>
                    <IconFontAwesome
                      name="faArrowRight"
                      color={colorTwoHex}
                      size="sm"
                    />
                  </Box>
                </ButtonMui>
                {showLanguageToggle && (
                  <LanguageToggle
                    sx={(theme) => ({
                      marginTop: theme.spacing(showJoinButton ? 0 : 1),
                    })}
                  />
                )}
              </Box>
            </Stack>
          )}
        </Box>
      </Box>
    </OfficeHeaderWrapper>
  );
};

export default OfficeHeader;
